import { useEffect, useState } from "react";
import { Link, useParams, useNavigate, Navigate } from "react-router-dom";
import { LayoutDashboard, User, ShoppingBag, Download, LogOut, Calendar, GraduationCap, Award } from "lucide-react";
import { userApi, money, formatApiErrorDetail } from "../lib/api";
import { track } from "../lib/analytics";
import { useUser } from "../context/UserAuthContext";
import Seo from "../components/Seo";
import { toast } from "sonner";

const NAV = [
  { key: "", label: "Panelim", icon: LayoutDashboard },
  { key: "etkinliklerim", label: "Etkinliklerim", icon: Calendar },
  { key: "siparislerim", label: "Siparişlerim", icon: ShoppingBag },
  { key: "dijital-urunlerim", label: "Dijital Ürünlerim", icon: Download },
  { key: "profilim", label: "Profilim", icon: User },
];

export function ProtectedUser({ children }) {
  const { user, ready } = useUser();
  if (!ready) return <div className="min-h-screen flex items-center justify-center bg-brand-cream text-brand-text/50">Yükleniyor…</div>;
  if (!user) return <Navigate to="/giris?next=/hesabim" replace />;
  return children;
}

export default function Account() {
  const { section = "" } = useParams();
  const { user, logout, refresh } = useUser();
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-brand-cream pt-24 pb-16">
      <Seo title="Hesabım" />
      <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-4 gap-8">
        <aside className="lg:col-span-1">
          <Link to="/" className="block font-serif text-2xl font-semibold text-brand-green mb-4">BİLGECE<span className="text-brand-gold align-super text-sm">™</span></Link>
          <div className="bg-brand-green text-brand-offwhite rounded-sm p-5 mb-4">
            <p className="text-brand-offwhite/60 text-xs">Merhaba,</p>
            <p className="font-serif text-2xl">{user?.first_name || user?.name}</p>
          </div>
          <nav className="bg-brand-offwhite border border-brand-gold-light/40 rounded-sm p-2">
            {NAV.map((n) => {
              const Icon = n.icon; const active = section === n.key;
              return <Link key={n.key} to={`/hesabim/${n.key}`} data-testid={`account-nav-${n.key || "dashboard"}`}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-sm text-sm ${active ? "bg-brand-gold text-brand-offwhite" : "text-brand-text/70 hover:bg-brand-cream"}`}><Icon size={17} /> {n.label}</Link>;
            })}
            <button onClick={() => { logout(); nav("/"); }} data-testid="account-logout" className="w-full flex items-center gap-3 px-4 py-2.5 rounded-sm text-sm text-brand-text/70 hover:bg-brand-cream"><LogOut size={17} /> Çıkış Yap</button>
          </nav>
        </aside>
        <main className="lg:col-span-3">
          {section === "" && <Dashboard />}
          {section === "etkinliklerim" && <MyEvents />}
          {section === "siparislerim" && <Orders />}
          {section === "dijital-urunlerim" && <Digital />}
          {section === "profilim" && <Profile user={user} refresh={refresh} />}
        </main>
      </div>
    </div>
  );
}

function MyEvents() {
  const [regs, setRegs] = useState(null);
  const load = () => userApi.get("/account/events").then(({ data }) => setRegs(data)).catch(() => setRegs([]));
  useEffect(() => { load(); }, []);
  const cancel = async (id) => {
    if (!window.confirm("Kaydını iptal etmek istiyor musun?")) return;
    try { await userApi.delete(`/account/events/${id}`); toast.success("Kayıt iptal edildi."); load(); }
    catch { toast.error("İptal başarısız."); }
  };
  const badge = { confirmed: "bg-brand-green text-brand-offwhite", pending: "bg-brand-gold-light text-brand-green", waitlisted: "bg-brand-gold-light text-brand-green" };
  return (
    <div data-testid="account-events">
      <h1 className="font-serif text-4xl text-brand-green mb-6">Etkinliklerim</h1>
      {!regs ? <p className="text-brand-text/50">Yükleniyor…</p> : regs.length === 0 ? <p className="text-brand-text/60">Henüz bir etkinlik kaydın yok. <Link to="/etkinlikler" className="text-brand-gold">Etkinliklere göz at</Link></p> : (
        <div className="space-y-4">
          {regs.map((r) => (
            <div key={r.id} data-testid={`myevent-${r.id}`} className="flex justify-between items-center bg-brand-offwhite border border-brand-gold-light/40 rounded-sm p-5">
              <div>
                <p className="font-serif text-xl text-brand-green">{r.event_title}</p>
                <span className={`inline-block mt-1 text-xs px-2 py-1 rounded-full ${badge[r.status] || "bg-brand-cream"}`}>{r.status}{r.status === "waitlisted" && r.position ? ` · sıra ${r.position}` : ""}</span>
              </div>
              <button onClick={() => cancel(r.id)} data-testid={`cancel-reg-${r.id}`} className="text-sm text-red-700 hover:underline">İptal</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Card({ icon: Icon, label, value, muted }) {
  return <div className={`rounded-sm p-6 border ${muted ? "bg-brand-offwhite/60 border-brand-gold-light/30" : "bg-brand-offwhite border-brand-gold-light/40"}`}>
    <Icon className="text-brand-gold" size={22} /><div className="font-serif text-3xl text-brand-green mt-3">{value}</div><div className="text-brand-text/60 text-sm">{label}</div></div>;
}

function Dashboard() {
  const [d, setD] = useState(null);
  const [ev, setEv] = useState(0);
  useEffect(() => {
    userApi.get("/account/dashboard").then(({ data }) => setD(data)).catch(() => {});
    userApi.get("/account/events").then(({ data }) => setEv(data.length)).catch(() => {});
  }, []);
  return (
    <div data-testid="account-dashboard">
      <h1 className="font-serif text-4xl text-brand-green mb-6">Panelim</h1>
      <div className="grid sm:grid-cols-3 gap-5 mb-6">
        <Card icon={ShoppingBag} label="Siparişlerim" value={d?.orders ?? "…"} />
        <Card icon={Download} label="Dijital Ürünlerim" value={d?.digital ?? "…"} />
        <Card icon={Calendar} label="Yaklaşan Etkinliklerim" value={ev} />
      </div>
      <div className="grid sm:grid-cols-3 gap-5">
        <Card icon={GraduationCap} label="Eğitimlerim (yakında)" value="—" muted />
        <Card icon={Award} label="Sertifikalarım (yakında)" value="—" muted />
        <Card icon={User} label="Favorilerim (yakında)" value="—" muted />
      </div>
    </div>
  );
}

function Orders() {
  const [orders, setOrders] = useState(null);
  useEffect(() => { userApi.get("/orders").then(({ data }) => setOrders(data)).catch(() => setOrders([])); }, []);
  const badge = { paid: "bg-brand-green text-brand-offwhite", pending: "bg-brand-gold-light text-brand-green", processing: "bg-brand-gold-light text-brand-green", failed: "bg-red-100 text-red-700" };
  return (
    <div data-testid="account-orders">
      <h1 className="font-serif text-4xl text-brand-green mb-6">Siparişlerim</h1>
      {!orders ? <p className="text-brand-text/50">Yükleniyor…</p> : orders.length === 0 ? <p className="text-brand-text/60">Henüz siparişin yok.</p> : (
        <div className="space-y-4">
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-${o.order_number}`} className="bg-brand-offwhite border border-brand-gold-light/40 rounded-sm p-5">
              <div className="flex justify-between items-start">
                <div><p className="font-medium text-brand-green">{o.order_number}</p><p className="text-brand-text/50 text-xs">{(o.created_at || "").slice(0, 10)}</p></div>
                <span className={`text-xs px-2 py-1 rounded-full ${badge[o.status] || "bg-brand-cream"}`}>{o.status}</span>
              </div>
              <div className="mt-3 text-sm text-brand-text/70">{o.items.map((i) => `${i.name} × ${i.quantity}`).join(", ")}</div>
              <div className="mt-2 font-semibold text-brand-green">{money(o.total)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Digital() {
  const [ents, setEnts] = useState(null);
  useEffect(() => { userApi.get("/account/digital").then(({ data }) => setEnts(data)).catch(() => setEnts([])); }, []);
  const download = async (ent) => {
    try {
      const res = await userApi.get(`/account/digital/${ent.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = ent.product_name || "bilgece"; a.click();
      track("digital_download", { product: ent.product_name });
    } catch { toast.error("İndirme başarısız."); }
  };
  return (
    <div data-testid="account-digital">
      <h1 className="font-serif text-4xl text-brand-green mb-6">Dijital Ürünlerim</h1>
      {!ents ? <p className="text-brand-text/50">Yükleniyor…</p> : ents.length === 0 ? <p className="text-brand-text/60">Henüz dijital ürünün yok.</p> : (
        <div className="space-y-4">
          {ents.map((e) => (
            <div key={e.id} data-testid={`entitlement-${e.id}`} className="flex justify-between items-center bg-brand-offwhite border border-brand-gold-light/40 rounded-sm p-5">
              <span className="font-serif text-xl text-brand-green">{e.product_name}</span>
              <button onClick={() => download(e)} data-testid={`download-${e.id}`} className="inline-flex items-center gap-2 bg-brand-gold text-brand-offwhite px-5 py-2.5 rounded-sm text-sm font-semibold"><Download size={16} /> İndir</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Profile({ user, refresh }) {
  const inp = "w-full px-4 py-3 text-sm rounded-sm border border-brand-gold-light/50 bg-white outline-none focus:border-brand-gold";
  const [f, setF] = useState({ first_name: user.first_name || "", last_name: user.last_name || "", phone: user.phone || "", city: user.city || "", marketing_consent: !!user.marketing_consent });
  const save = async (e) => {
    e.preventDefault();
    try { await userApi.put("/auth/profile", f); await refresh(); toast.success("Profil güncellendi."); }
    catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail)); }
  };
  return (
    <div data-testid="account-profile">
      <h1 className="font-serif text-4xl text-brand-green mb-6">Profilim</h1>
      <form onSubmit={save} className="bg-brand-offwhite border border-brand-gold-light/40 rounded-sm p-8 space-y-4 max-w-lg">
        <div className="grid sm:grid-cols-2 gap-4">
          <input placeholder="Ad" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} className={inp} data-testid="profile-first" />
          <input placeholder="Soyad" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} className={inp} />
        </div>
        <input disabled value={user.email} className={inp + " opacity-60"} />
        <div className="grid sm:grid-cols-2 gap-4">
          <input placeholder="Telefon" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={inp} />
          <input placeholder="Şehir" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className={inp} />
        </div>
        <label className="flex items-center gap-2 text-sm text-brand-text/70"><input type="checkbox" checked={f.marketing_consent} onChange={(e) => setF({ ...f, marketing_consent: e.target.checked })} className="accent-brand-gold" /> Pazarlama e-postalarını almak istiyorum</label>
        <button data-testid="profile-save" className="bg-brand-gold text-brand-offwhite px-6 py-2.5 rounded-sm text-sm font-semibold">Kaydet</button>
      </form>
    </div>
  );
}
